import { useState } from "react";

import LoadingButton from "@/components/loading-button";
import { useComponentTranslation } from "@/services/i18n";
import LinkedInField from "./fields/LinkedInField";
import PortfolioField from "./fields/PortfolioField";
import PhoneField from "./fields/PhoneField";
import CoverLetterField from "./fields/CoverLetterField";
import AddressField from "./fields/AddressField";
import CityField from "./fields/CityField";
import CountryField from "./fields/CountryField";
import AvailabilityField from "./fields/AvailabilityField";
import SalaryExpectationsField from "./fields/SalaryExpectationsField";
import ResumeField from "./fields/ResumeField";
import CustomQuestionsField from "./fields/CustomQuestionsField";

const translations = {
  en: {
    auth: {
      register: {
        firstname: "First name",
        lastname: "Last name",
        email: "Email",
        resume: "Resume",
        resume_upload: "Upload resume",
        resume_only_pdf: "only PDF",
        firstname_placeholder: "Enter your first name",
        lastname_placeholder: "Enter your last name",
        email_placeholder: "example@mail.com",
        button: "Apply",
        missing_fields: "Please fill in the following fields:",
        complete_profile: "Complete your profile to apply",
        review_info: "Please review your information below",
        fields: {
          firstname: "First name",
          lastname: "Last name",
          email: "Email",
          resume: "Resume",
          linkedin: "LinkedIn Profile",
          portfolio: "Portfolio",
          phone: "Phone",
          cover_letter: "Cover Letter",
          address: "Address",
          city: "City",
          country: "Country",
          availability: "Availability",
          salary_expectations: "Salary Expectations",
        },
      },
    },
  },
  fr: {
    auth: {
      register: {
        firstname: "Prénom",
        lastname: "Nom",
        email: "Email",
        resume: "CV",
        resume_upload: "Télécharger le CV",
        resume_only_pdf: "PDF uniquement",
        firstname_placeholder: "Entrez votre prénom",
        lastname_placeholder: "Entrez votre nom",
        email_placeholder: "exemple@mail.com",
        button: "Postuler",
        missing_fields: "Veuillez remplir les champs suivants :",
        complete_profile: "Complétez votre profil pour postuler",
        review_info: "Veuillez vérifier vos informations ci-dessous",
        fields: {
          firstname: "Prénom",
          lastname: "Nom",
          email: "Email",
          resume: "CV",
          linkedin: "Profil LinkedIn",
          portfolio: "Portfolio",
          phone: "Téléphone",
          cover_letter: "Lettre de motivation",
          address: "Adresse",
          city: "Ville",
          country: "Pays",
          availability: "Disponibilité",
          salary_expectations: "Prétentions salariales",
        },
      },
    },
  },
};

export default function CompleteProfileForm({ user, onSubmit, loading, job }) {
  const { t } = useComponentTranslation(translations);

  const visibleFields = job?.application_form_fields?.filter((field) => field.visible === true) || [];

  // Initialize values from user object
  const initialValues = {
    firstname: user?.firstname || "",
    lastname: user?.lastname || "",
    email: user?.email || "",
    resume: user?.resume_url || user?.resume || "",
    linkedin: user?.linkedin || "",
    portfolio: user?.portfolio || "",
    phone: user?.phone || "",
    cover_letter: user?.cover_letter || "",
    address: user?.address || "",
    city: user?.city || "",
    country: user?.country || "",
    availability: user?.availability || "",
    salary_expectations: user?.salary_expectations || "",
  };

  const [values, setValues] = useState(initialValues);
  const [showValidation, setShowValidation] = useState(false);

  // Check if a field is empty
  const isFieldEmpty = (value) => {
    return !value || (typeof value === "string" && value.trim() === "");
  };

  // Get human-readable field label
  const getFieldLabel = (fieldId) => {
    // Check if it's a custom question
    if (fieldId.startsWith("custom_")) {
      const questionId = fieldId.replace("custom_", "");
      const question = (job?.custom_questions || []).find((q) => q.id === questionId);
      return question?.question || fieldId;
    }
    
    // Field labels - hardcoded for reliability
    const fieldLabels = {
      en: {
        firstname: "First name",
        lastname: "Last name",
        email: "Email",
        resume: "Resume",
        linkedin: "LinkedIn Profile",
        portfolio: "Portfolio",
        phone: "Phone",
        cover_letter: "Cover Letter",
        address: "Address",
        city: "City",
        country: "Country",
        availability: "Availability",
        salary_expectations: "Salary Expectations",
      },
      fr: {
        firstname: "Prénom",
        lastname: "Nom",
        email: "Email",
        resume: "CV",
        linkedin: "Profil LinkedIn",
        portfolio: "Portfolio",
        phone: "Téléphone",
        cover_letter: "Lettre de motivation",
        address: "Adresse",
        city: "Ville",
        country: "Pays",
        availability: "Disponibilité",
        salary_expectations: "Prétentions salariales",
      },
    };

    // Get current language (default to 'en')
    const lang = typeof window !== "undefined" && window.localStorage?.getItem("i18nextLng")?.startsWith("fr") ? "fr" : "en";
    const labels = fieldLabels[lang] || fieldLabels.en;

    if (labels[fieldId]) {
      return labels[fieldId];
    }
    // Fallback: convert field_id to "Field Id" format
    return fieldId.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Get list of missing mandatory fields with their labels
  const getMissingMandatoryFields = () => {
    const missing = [];

    // Check firstname, lastname, email
    if (isFieldEmpty(values.firstname)) {
      missing.push({ id: "firstname", label: getFieldLabel("firstname") });
    }
    if (isFieldEmpty(values.lastname)) {
      missing.push({ id: "lastname", label: getFieldLabel("lastname") });
    }
    if (isFieldEmpty(values.email)) {
      missing.push({ id: "email", label: getFieldLabel("email") });
    }

    // Check all visible mandatory fields
    const mandatoryFields = visibleFields.filter((field) => field.mandatory === true);
    for (const field of mandatoryFields) {
      // Skip fullname - we already require firstname and lastname separately
      if (field.id === "fullname") continue;

      const value = values[field.id];
      if (isFieldEmpty(value)) {
        missing.push({ id: field.id, label: getFieldLabel(field.id) });
      }
    }

    // Check required custom questions
    const requiredQuestions = (job?.custom_questions || []).filter((q) => q.required);
    for (const question of requiredQuestions) {
      const value = values[`custom_${question.id}`];
      if (isFieldEmpty(value)) {
        missing.push({ id: `custom_${question.id}`, label: question.question });
      }
    }

    return missing;
  };

  // Check if all mandatory fields are filled
  const isFormValid = () => {
    return getMissingMandatoryFields().length === 0;
  };

  // Prepare form data for submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowValidation(true);

    if (!isFormValid()) {
      return;
    }

    onSubmit(values);
  };

  return (
    <form onSubmit={handleSubmit} className="w-full flex flex-col gap-y-6">
      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-900">{t("auth.register.complete_profile")}</h3>
        <p className="text-sm text-gray-600 mt-1">{t("auth.register.review_info")}</p>
      </div>

      {/* Firstname and lastname - always show */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstname" className="input-label">
            {t("auth.register.firstname")} *
          </label>
          <input
            type="text"
            id="firstname"
            name="firstname"
            value={values.firstname || ""}
            onChange={(e) => setValues({ ...values, firstname: e.target.value })}
            className={`input ${showValidation && isFieldEmpty(values.firstname) ? "border-red-500" : ""}`}
            placeholder={t("auth.register.firstname_placeholder")}
          />
        </div>
        <div>
          <label htmlFor="lastname" className="input-label">
            {t("auth.register.lastname")} *
          </label>
          <input
            type="text"
            id="lastname"
            name="lastname"
            value={values.lastname || ""}
            onChange={(e) => setValues({ ...values, lastname: e.target.value })}
            className={`input ${showValidation && isFieldEmpty(values.lastname) ? "border-red-500" : ""}`}
            placeholder={t("auth.register.lastname_placeholder")}
          />
        </div>
      </div>

      {/* Email - always show */}
      <div>
        <label htmlFor="email" className="input-label">
          {t("auth.register.email")} *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={values.email || ""}
          onChange={(e) => setValues({ ...values, email: e.target.value })}
          className={`input ${showValidation && isFieldEmpty(values.email) ? "border-red-500" : ""}`}
          placeholder={t("auth.register.email_placeholder")}
        />
      </div>

      {/* Dynamic fields from application_form_fields - show all visible fields */}
      <ResumeField visibleFields={visibleFields} value={values.resume} setValue={(val) => setValues({ ...values, resume: val })} showValidation={showValidation} />
      <LinkedInField visibleFields={visibleFields} value={values.linkedin} onChange={(e) => setValues({ ...values, linkedin: e.target.value })} showValidation={showValidation} />
      <PortfolioField
        visibleFields={visibleFields}
        value={values.portfolio}
        onChange={(e) => setValues({ ...values, portfolio: e.target.value })}
        showValidation={showValidation}
      />
      <PhoneField visibleFields={visibleFields} value={values.phone} onChange={(e) => setValues({ ...values, phone: e.target.value })} showValidation={showValidation} />
      <CoverLetterField
        visibleFields={visibleFields}
        value={values.cover_letter}
        onChange={(e) => setValues({ ...values, cover_letter: e.target.value })}
        showValidation={showValidation}
      />
      <AddressField visibleFields={visibleFields} value={values.address} onChange={(e) => setValues({ ...values, address: e.target.value })} showValidation={showValidation} />
      <CityField visibleFields={visibleFields} value={values.city} onChange={(e) => setValues({ ...values, city: e.target.value })} showValidation={showValidation} />
      <CountryField visibleFields={visibleFields} value={values.country} onChange={(e) => setValues({ ...values, country: e.target.value })} showValidation={showValidation} />
      <AvailabilityField
        visibleFields={visibleFields}
        value={values.availability}
        onChange={(e) => setValues({ ...values, availability: e.target.value })}
        showValidation={showValidation}
      />
      <SalaryExpectationsField
        visibleFields={visibleFields}
        value={values.salary_expectations}
        onChange={(e) => setValues({ ...values, salary_expectations: e.target.value })}
        showValidation={showValidation}
      />

      {/* Custom Questions - always show for this job */}
      <CustomQuestionsField
        questions={job?.custom_questions}
        values={values}
        setValues={setValues}
        showValidation={showValidation}
      />

      {/* Validation warning */}
      {showValidation && !isFormValid() && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          <p className="font-medium mb-2">{t("auth.register.missing_fields")}</p>
          <ul className="list-disc list-inside space-y-1">
            {getMissingMandatoryFields().map((field) => (
              <li key={field.id}>{field.label}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Submit Button */}
      <LoadingButton loading={loading} className="btn btn-primary w-full btn-large">
        {t("auth.register.button")}
      </LoadingButton>
    </form>
  );
}
